import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PRODUCT_DOMAINS } from "@/constants/company";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  company: "",
  contact_name: "",
  email: "",
  product_domain: PRODUCT_DOMAINS[0],
  volume: "",
  destination_port: "",
  message: "",
};

const fieldCls =
  "border-navy-600 bg-navy-800 text-white placeholder:text-slate-500 focus-visible:ring-brand";

export default function EnquiryForm({ defaultDomain }) {
  const [form, setForm] = useState({ ...EMPTY, product_domain: defaultDomain || PRODUCT_DOMAINS[0] });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      const history = JSON.parse(localStorage.getItem("et_enquiries") || "[]");
      history.unshift({ ...form, created_at: new Date().toISOString() });
      localStorage.setItem("et_enquiries", JSON.stringify(history.slice(0, 10)));
      setSubmitted(true);
      setForm({ ...EMPTY, product_domain: form.product_domain });
      toast.success("Enquiry received. Our trading desk will respond shortly.");
      window.dispatchEvent(new Event("et-enquiry-saved"));
    } catch (err) {
      toast.error("Could not submit your enquiry. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="flex flex-col items-center gap-4 rounded-lg border border-brand/40 bg-navy-800 p-10 text-center"
        data-testid="enquiry-success-alert"
      >
        <CheckCircle2 className="h-12 w-12 text-brand" />
        <h3 className="font-heading text-xl font-bold text-white">Enquiry received</h3>
        <p className="max-w-md text-sm text-slate-400">
          Thank you. Your request has been logged with our trading desk — we typically respond within one
          business day with pricing and availability.
        </p>
        <Button
          variant="outline"
          className="border-navy-600 text-slate-300 hover:bg-navy-700 hover:text-white"
          onClick={() => setSubmitted(false)}
          data-testid="enquiry-new-button"
        >
          Submit another enquiry
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-5 sm:grid-cols-2" data-testid="enquiry-form">
      <div>
        <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Company *</label>
        <Input required value={form.company} onChange={set("company")} placeholder="Your company name"
          className={fieldCls} data-testid="enquiry-company-input" />
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Contact person *</label>
        <Input required value={form.contact_name} onChange={set("contact_name")} placeholder="Full name"
          className={fieldCls} data-testid="enquiry-name-input" />
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Business email *</label>
        <Input required type="email" value={form.email} onChange={set("email")} placeholder="name@company.com"
          className={fieldCls} data-testid="enquiry-email-input" />
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Product domain *</label>
        <select
          required
          value={form.product_domain}
          onChange={set("product_domain")}
          className="h-10 w-full rounded-md border border-navy-600 bg-navy-800 px-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand"
          data-testid="enquiry-domain-select"
        >
          {PRODUCT_DOMAINS.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Estimated volume</label>
        <Input value={form.volume} onChange={set("volume")} placeholder="e.g. 2,000 MT / month"
          className={fieldCls} data-testid="enquiry-volume-input" />
      </div>
      <div>
        <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Destination port</label>
        <Input value={form.destination_port} onChange={set("destination_port")} placeholder="e.g. Port Klang, Malaysia"
          className={fieldCls} data-testid="enquiry-port-input" />
      </div>
      <div className="sm:col-span-2">
        <label className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400">Requirements *</label>
        <Textarea required rows={5} value={form.message} onChange={set("message")}
          placeholder="Product grade, specification, packaging, Incoterms, target price..."
          className={fieldCls} data-testid="enquiry-message-input" />
      </div>
      <div className="sm:col-span-2">
        <Button
          type="submit"
          disabled={loading}
          className="w-full bg-brand font-semibold text-navy transition-transform duration-200 hover:scale-[1.01] hover:bg-brand hover:shadow-[0_0_24px_rgba(0,208,132,0.35)] sm:w-auto sm:px-10"
          data-testid="enquiry-submit-button"
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          {loading ? "Submitting..." : "Submit enquiry"}
        </Button>
      </div>
    </form>
  );
}
